import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
 Search,
 Flame, Sparkles, Crown, BadgeCheck, Truck, Zap, Pizza, Star,
} from 'lucide-react';
import { LanguageProvider } from '../context/LanguageContext';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import RestaurantCard from '../components/RestaurantCard';
import MarketplaceRow from '../components/MarketplaceRow';
import FeaturedCategories from '../components/FeaturedCategories';
import RequestRestaurantCard from '../components/RequestRestaurantCard';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DirectoryInner = () => {
 const [searchParams] = useSearchParams();
 // Accept either `q` (preferred — name/city/area/street/zip) or the legacy
 // `zip` param coming from older bookmarks / the waitlist card.
 const queryParam = (searchParams.get('q') || searchParams.get('zip') || '').trim();
 const isSearchMode =!!queryParam;

 // Classify the query so the "Request this restaurant" form can pre-fill
 // smart defaults (5-digit numeric → ZIP, words → restaurant name).
 const looksLikeZip = /^\d{4,6}$/.test(queryParam);
 const prefill = {
 restaurant: looksLikeZip? '': queryParam,
 zip: looksLikeZip? queryParam: '',
 city: '',
 };

 const [restaurants, setRestaurants] = useState([]);
 const [cuisines, setCuisines] = useState([]);
 const [selectedCuisine, setSelectedCuisine] = useState('');
 const [q, setQ] = useState('');
 const [fastOnly, setFastOnly] = useState(false);
 const [loading, setLoading] = useState(true);
 const [emptyResults, setEmptyResults] = useState(false);

 useEffect(() => {
 if (!isSearchMode) {
 axios.get(`${API}/public/cuisines`).then(r => setCuisines(r.data.cuisines || []));
 }
 }, [isSearchMode]);

 useEffect(() => {
 setLoading(true);
 if (isSearchMode) {
 axios
.get(`${API}/restaurants/search?q=${encodeURIComponent(queryParam)}`)
.then((r) => {
 const list = r.data.restaurants || [];
 setRestaurants(list);
 setEmptyResults(list.length === 0);
 })
.catch(() => { setRestaurants([]); setEmptyResults(true); })
.finally(() => setLoading(false));
 return;
 }
 const timer = setTimeout(() => {
 const params = new URLSearchParams();
 if (selectedCuisine) params.set('cuisine', selectedCuisine);
 if (q) params.set('q', q);
 if (fastOnly) params.set('fast_only', 'true');
 axios.get(`${API}/public/restaurants?${params}`)
.then(r => setRestaurants(r.data.restaurants || []))
.finally(() => setLoading(false));
 }, 250);
 return () => clearTimeout(timer);
 }, [selectedCuisine, q, fastOnly, isSearchMode, queryParam]);

 // Marketplace rows — themed cuts of the same dataset for "active marketplace" perception.
 // Each row reuses restaurants dynamically; harmless if dataset is small.
 const rows = useMemo(() => {
 const list = Array.isArray(restaurants)? restaurants: [];
 if (list.length === 0) {
 return { trending: [], topRated: [], popular: [], fast: [], freeDelivery: [], newest: [], pizza: [], localFavorites: [] };
 }
 const byRating = [...list].sort((a, b) =>
 (b.rating_avg || 0) - (a.rating_avg || 0) ||
 (b.rating_count || 0) - (a.rating_count || 0)
 );
 const byVotes = [...list].sort((a, b) => (b.rating_count || 0) - (a.rating_count || 0));
 const byNewest = [...list].sort((a, b) =>
 String(b.created_at || '').localeCompare(String(a.created_at || ''))
 );
 const free = list.filter((r) => (r.delivery_fee || 0) === 0 && r.accepts_delivery!== false);
 const fast = list.filter((r) => {
 // Prefer the new per-method ETAs (pickup/delivery_avg_minutes). Fall
 // back to parsing the legacy `estimated_time` string for restaurants
 // that haven't migrated yet. A restaurant is "fast" if the BEST
 // available method is ≤ 30 minutes.
 const candidates = [];
 if ((r.pickup_enabled?? r.accepts_pickup) && r.pickup_avg_minutes) candidates.push(r.pickup_avg_minutes);
 if ((r.delivery_enabled?? r.accepts_delivery) && r.delivery_avg_minutes) candidates.push(r.delivery_avg_minutes);
 if (candidates.length) return Math.min(...candidates) <= 30;
 const m = String(r.estimated_time || '').match(/(\d+)/);
 return m && parseInt(m[1], 10) <= 30;
 });
 const pizza = list.filter((r) => {
 // Match against the new cuisine_types array OR the legacy string.
 const all = Array.isArray(r.cuisine_types) && r.cuisine_types.length
? r.cuisine_types.join(' ')
: (r.cuisine_type || '');
 return /pizza|italian/i.test(all);
 });
 // Trending = popular by votes (cyclic shuffle for variety even with small N)
 const trending = byVotes.length? [...byVotes.slice(2),...byVotes.slice(0, 2)]: [];
 // Local favorites = mid-cut combining rating + votes
 const localFavorites = [...list].sort((a, b) =>
 ((b.rating_avg || 0) * Math.log(2 + (b.rating_count || 0))) -
 ((a.rating_avg || 0) * Math.log(2 + (a.rating_count || 0)))
 );

 return {
 trending: trending.slice(0, 6),
 topRated: byRating.slice(0, 6),
 popular: byVotes.slice(0, 6),
 fast: (fast.length? fast: list).slice(0, 6),
 freeDelivery: (free.length? free: list).slice(0, 6),
 newest: byNewest.slice(0, 6),
 pizza: pizza.slice(0, 6),
 localFavorites: localFavorites.slice(0, 6),
 };
 }, [restaurants]);

 return (
 <div className="min-h-screen bg-black">
 <SEO
 title={isSearchMode? `Restaurants matching ${queryParam} — 0% Commission Ordering`: 'Find a Restaurant — 0% Commission Ordering'}
 description={isSearchMode
? `Discover restaurants on NoFee matching "${queryParam}". Order directly, 0% commission, pay with Stripe.`
: 'Browse 90+ food categories on NoFee — pizza, sushi, burgers, tacos, healthy bowls and more. 0% commission · direct ordering.'}
 path={isSearchMode? `/restaurants?q=${queryParam}`: '/restaurants'}
 jsonLd={{
 "@context": "https://schema.org",
 "@type": "CollectionPage",
 "name": isSearchMode? `Search · ${queryParam}`: 'Restaurants on NoFee',
 "url": isSearchMode
? `https://nofee.restaurant/restaurants?q=${encodeURIComponent(queryParam)}`
: 'https://nofee.restaurant/restaurants',
 "isPartOf": { "@type": "WebSite", "name": "NoFee", "url": "https://nofee.restaurant" },
 "breadcrumb": {
 "@type": "BreadcrumbList",
 "itemListElement": [
 { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://nofee.restaurant/" },
 { "@type": "ListItem", "position": 2, "name": "Restaurants", "item": "https://nofee.restaurant/restaurants" },
 ],
 },
 "mainEntity": {
 "@type": "ItemList",
 "numberOfItems": restaurants.length,
 "itemListElement": restaurants.slice(0, 20).map((r, i) => ({
 "@type": "ListItem",
 "position": i + 1,
 "url": `https://nofee.restaurant/menu/${r.slug}`,
 "name": r.name,
 })),
 },
 }}
 />
 <Navbar />

 {/* Hero header */}
 <section className="pt-28 pb-12 bg-gradient-to-b from-neutral-950 to-black">
 <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
 <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3" data-testid="directory-title">
 {isSearchMode? (
 <>
 Results for <span className="text-gold-500">&ldquo;{queryParam}&rdquo;</span>
 </>
 ): (
 <>Find a <span className="text-gold-500">restaurant</span></>
 )}
 </h1>
 <p className="text-white/50 max-w-xl mx-auto">
 All restaurants on NoFee · 0% commission · Direct ordering
 </p>

 {/* Search (only in non-search mode) */}
 {!isSearchMode && (
 <div className="mt-8 max-w-xl mx-auto relative">
 <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
 <input
 type="search"
 value={q}
 onChange={e => setQ(e.target.value)}
 placeholder="Search by ZIP code, city, area, street, or restaurant name"
 className="w-full pl-12 pr-4 py-4 bg-neutral-900 border border-white/10 text-white placeholder:text-white/30 rounded-2xl focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none"
 data-testid="directory-search"
 />
 </div>
 )}
 </div>
 </section>

 {/* Featured categories — premium marketplace grid + "View all" modal */}
 {!isSearchMode && (
 <section className="bg-black pt-8 pb-2">
 <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
 <FeaturedCategories
 title="Browse by category"
 subtitle="Find what you crave"
 />
 </div>
 </section>
 )}

 {/* Cuisine filter chips (legacy, still useful for fine-grained filtering) */}
 {!isSearchMode && cuisines.length > 0 && (
 <section className="bg-black sticky top-16 z-10 backdrop-blur border-b border-white/[0.04]">
 <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex gap-2 overflow-x-auto scrollbar-hide">
 <button
 onClick={() => setSelectedCuisine('')}
 className={`px-3.5 py-1.5 rounded-full text-[12px] font-bold whitespace-nowrap transition-colors ${
!selectedCuisine? 'bg-gold-500 text-black': 'bg-white/5 text-white/65 hover:bg-white/10'
 }`}
 data-testid="cuisine-all"
 >
 All ({restaurants.length})
 </button>
 {/* Fast Restaurants Only — toggleable chip styled in NoFee
 gold-on-black so customers can immediately spot trusted
 restaurants. Mirrors the cuisine chip UX. */}
 <button
 onClick={() => setFastOnly((v) =>!v)}
 className={`px-3.5 py-1.5 rounded-full text-[12px] font-bold whitespace-nowrap transition-colors inline-flex items-center gap-1 ${
 fastOnly
? 'bg-yellow-400 text-black shadow-[0_0_0_2px_rgba(250,204,21,0.18)]'
: 'bg-white/5 text-yellow-300 hover:bg-white/10 border border-yellow-400/30'
 }`}
 data-testid="filter-fast-only"
 aria-pressed={fastOnly}
 >
 ⚡ Fast Restaurants Only
 </button>
 {cuisines.map(c => (
 <button
 key={c.name}
 onClick={() => setSelectedCuisine(c.name)}
 className={`px-3.5 py-1.5 rounded-full text-[12px] font-bold whitespace-nowrap transition-colors ${
 selectedCuisine === c.name? 'bg-gold-500 text-black': 'bg-white/5 text-white/65 hover:bg-white/10'
 }`}
 data-testid={`cuisine-${c.name}`}
 >
 {c.name} ({c.count})
 </button>
 ))}
 </div>
 </section>
 )}

 {/* Restaurant grid OR empty-zip waitlist */}
 <section className="py-10 lg:py-12">
 <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
 {loading? (
 <div className="text-center py-20"><div className="inline-block w-8 h-8 border-4 border-gold-500 border-t-transparent rounded-full animate-spin" /></div>
 ): isSearchMode && emptyResults? (
 <RequestRestaurantCard
 prefillRestaurantName={prefill.restaurant}
 prefillZip={prefill.zip}
 prefillCity={prefill.city}
 />
 ): restaurants.length === 0? (
 <RequestRestaurantCard />
 ): isSearchMode || selectedCuisine || q? (
 // Filtered/ZIP mode → single dense grid
 <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 2xl:grid-cols-6 gap-3 lg:gap-4" data-testid="restaurant-grid">
 {restaurants.map((r) => (
 <RestaurantCard key={r.id} r={r} />
 ))}
 </div>
 ): (
 // Marketplace homepage mode → themed rows for higher perceived activity
 <div className="space-y-12 lg:space-y-14" data-testid="marketplace-rows">
 <MarketplaceRow
 title="Trending Near You"
 subtitle="Restaurants gaining momentum this week"
 Icon={Flame}
 iconClass="text-rose-400"
 restaurants={rows.trending}
 accentBadge="trending"
 testId="row-trending"
 />
 <MarketplaceRow
 title="Top Rated"
 subtitle="The highest-rated spots on NoFee"
 Icon={Crown}
 restaurants={rows.topRated}
 accentBadge="topRated"
 testId="row-top-rated"
 />
 <MarketplaceRow
 title="Popular This Week"
 subtitle="What customers are loving right now"
 Icon={Sparkles}
 restaurants={rows.popular}
 accentBadge="popular"
 testId="row-popular"
 />
 <MarketplaceRow
 title="Fast Delivery"
 subtitle="Ready in 30 minutes or less"
 Icon={Zap}
 iconClass="text-violet-300"
 restaurants={rows.fast}
 accentBadge="fast"
 testId="row-fast"
 />
 <MarketplaceRow
 title="Free Delivery"
 subtitle="No delivery fee · today"
 Icon={Truck}
 iconClass="text-blue-300"
 restaurants={rows.freeDelivery}
 accentBadge="freeDelivery"
 testId="row-free-delivery"
 />
 <MarketplaceRow
 title="New on NoFee"
 subtitle="Just joined the community"
 Icon={BadgeCheck}
 iconClass="text-emerald-300"
 restaurants={rows.newest}
 accentBadge="new"
 testId="row-new"
 />
 {rows.pizza.length > 0 && (
 <MarketplaceRow
 title="Pizza Near You"
 Icon={Pizza}
 restaurants={rows.pizza}
 testId="row-pizza"
 />
 )}
 <MarketplaceRow
 title="Local Favorites"
 subtitle="Most ordered by your neighbors"
 Icon={Star}
 restaurants={rows.localFavorites}
 testId="row-local"
 />

 {/* Full directory at the end */}
 <section>
 <h2 className="text-[20px] sm:text-[22px] lg:text-2xl font-extrabold text-white tracking-tight mb-4">
 All restaurants
 </h2>
 <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 2xl:grid-cols-6 gap-3 lg:gap-4">
 {restaurants.map((r) => (
 <RestaurantCard key={`all-${r.id}`} r={r} />
 ))}
 </div>
 </section>
 </div>
 )}
 </div>
 </section>

 <FooterSection />
 </div>
 );
};

const RestaurantsDirectory = () => (
 <LanguageProvider>
 <DirectoryInner />
 </LanguageProvider>
);

export default RestaurantsDirectory;
